/* eslint-disable arrow-body-style */

import { once } from 'lodash-es';

import { RECAPTCHA_V2_KEY } from '@/config/env';

const recaptchaKey = RECAPTCHA_V2_KEY;

/** Resolves when reCAPTCHA has finished loading. */
const waitForReady = once(
  () =>
    new Promise<void>((resolve) => {
      window.grecaptcha.ready(resolve);
    })
);

/**
 * Takes in an HTML element and binds Google reCAPTCHA to it.
 * @returns widgetId that can be used on grecaptcha.getResponse() and grecaptcha.execute()
 */
const getRecaptchaWidgetId = async (
  recaptchaElement: HTMLElement | string,
  callback: () => void
): Promise<string> => {
  if (!recaptchaKey) {
    return ''; // No recaptcha required if key is not set for localhost testing
  }

  await waitForReady();

  const recaptchaWidgetId = window.grecaptcha.render(recaptchaElement, {
    sitekey: recaptchaKey,

    // This callback is triggered after .getResponse()/.execute() is called in QuerySubmitLoginForm
    callback,
    size: 'invisible',
  });

  return recaptchaWidgetId;
};

/**
 * Takes in a widgetId that was generated from getRecaptchaWidgetId()
 * @returns reCAPTCHA response that needs to be passed together with a form to backend
 */
const getRecaptchaResponse = async (recaptchaWidgetId: string): Promise<string> => {
  if (!recaptchaKey) {
    return 'NOT_REQUIRED'; // Not required in localhost
  }

  await waitForReady();

  const recaptchaResponse = window.grecaptcha.getResponse(recaptchaWidgetId);

  if (!recaptchaResponse) {
    window.grecaptcha.execute(recaptchaWidgetId);
  }

  return recaptchaResponse;
};

/**
 * Returns true if reCAPTCHA is configured (by default there is no key on localhost)
 */
const isRecaptchaEnabled = (): boolean => {
  return !!recaptchaKey;
};

/**
 * Resets reCAPTCHA state on current Window object
 */
const resetRecaptcha = async (): Promise<void> => {
  try {
    if (isRecaptchaEnabled()) {
      await waitForReady();
      window.grecaptcha.reset();
    }
  } catch (recaptchaErr) {
    // ignore error
  }
};

export { getRecaptchaResponse, getRecaptchaWidgetId, isRecaptchaEnabled, resetRecaptcha };
